import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import Accordion from 'react-native-collapsible/Accordion';
import Text from '../Text';
import type { Section } from '~/utils/terms';

import ChevronDownSVG from '~/assets/svgs/chevron-down.svg';
import ChevronUpSVG from '~/assets/svgs/chevron-up.svg';
import colors from '~/assets/themes/colors';

interface Props {
  sections: Section[];
}

const Separator = () => <View className="h-[1px] bg-gray-200" />;

const AccordionGroup: React.FC<Props> = ({ sections }) => {
  const [activeSections, setActiveSections] = useState<number[]>([]);

  const renderHeader = useCallback(
    (section: Section, index: number) => {
      const sectionIndex = sections.findIndex(
        (find) => find.title === section.title
      );
      const isActive = activeSections.includes(sectionIndex);

      return (
        <>
          <View className="p-4 flex-row justify-between items-center">
            <Text className="font-medium text-sm text-gray-400">
              {section.title}
            </Text>

            {isActive ? (
              <ChevronUpSVG fill={colors.gray[400]} />
            ) : (
              <ChevronDownSVG fill={colors.gray[400]} />
            )}
          </View>
          {!isActive && index !== sections.length - 1 && <Separator />}
        </>
      );
    },
    [sections, activeSections]
  );

  const renderContent = useCallback(
    (
      content: Section,
      index: number,
      _isActive: boolean,
      _sections: Section[]
    ): React.ReactElement => {
      const sectionIndex = sections.findIndex(
        (find) => find.title === content.title
      );
      const isActive = activeSections.includes(sectionIndex);

      return (
        <>
          <View className="flex-1 p-4">
            <Text className="text-sm text-gray-400">
              {content.content.trim()}
            </Text>
          </View>
          {isActive && index !== sections.length - 1 && <Separator />}
        </>
      );
    },
    [activeSections, sections]
  );

  return (
    <View className="p-4 border border-gray-200 rounded-md">
      <Accordion
        expandMultiple
        sections={sections}
        activeSections={activeSections}
        renderHeader={renderHeader}
        renderContent={renderContent}
        underlayColor={'transparent'}
        onChange={setActiveSections}
      />
    </View>
  );
};

export default AccordionGroup;
